import { BalanceEntryResponse, Claim } from "../../api/ClientFileApi";

/**
 * The frontend half of the backend's ClaimBalance. A claim's balance history is a dated list of
 * absolute values rather than a running sum, so its balance is the value of its newest entry — and
 * both bulk actions have to decide the same thing about that entry the server does.
 */

/** The entry whose value the list shows as the claim's balance, or null for a claim without any. */
export function newestBalanceEntryOf(claim: Claim): BalanceEntryResponse | null {
  // The response lists a claim's entries oldest first, so the last one is its newest.
  return claim.balanceEntries[claim.balanceEntries.length - 1] ?? null;
}

/**
 * Whether the claim's newest entry is dated after `date`. Such a claim keeps the balance it displays
 * when an entry dated `date` is added: the new entry is filed into the middle of the history instead
 * of becoming the newest one.
 */
export function hasEntryNewerThan(claim: Claim, date: Date): boolean {
  const newest = newestBalanceEntryOf(claim);
  return newest !== null && startOfDay(newest.date) > startOfDay(date);
}

/**
 * Whether the claim's balance already stands at `date` or beyond — nothing left to carry forward to
 * it. A second entry sharing a date makes the balance of that day ambiguous, and an entry filed
 * behind a newer one changes nothing the list shows.
 */
export function hasEntryOnOrAfter(claim: Claim, date: Date): boolean {
  const newest = newestBalanceEntryOf(claim);
  return newest !== null && startOfDay(newest.date) >= startOfDay(date);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}
